// src/components/page/AboutPage.jsx
import React from "react";
import SEO from "../SEO";          // ← use your helper
import "./about.css";

export default function AboutPage() {
  // Safe helper to build canonical without SSR errors
  const origin =
    typeof window !== "undefined" && window.location?.origin
      ? window.location.origin
      : "https://werlocal.us"; // fallback to your live host
  const canonical = `${origin}/about`;

  const title = "About – We Are Local";
  const description =
    "We are an AI-powered news aggregator combined with real stories and insights from people. Our mission is simple: to deliver neutral news, practical guides, and useful event updates — written in a way that’s clear, positive, and easy to follow.\nWe’re a local community project designed for people who want to stay informed, understand what’s happening around them, and feel connected to their neighborhood — without getting dragged into bias or negativity.\n\n🚫 No ads.\n🚫 No pushing.\n🚫 No propaganda.\n\nJust the key facts, meaningful stories, and resources that matter most.";

  // Social preview image (replace with your real URL if you have one)
  const ogImage = `${origin}/og/we-are-local-about.jpg`;

  // JSON-LD blocks (multiple allowed by your SEO component)
  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "WebSite",
      "name": "We Are Local",
      "url": origin,
      "potentialAction": {
        "@type": "SearchAction",
        "target": `${origin}/search?q={query}`,
        "query-input": "required name=query"
      }
    },
    {
      "@context": "https://schema.org",
      "@type": "Organization",
      "name": "We Are Local",
      "url": origin,
      "logo": `${origin}/logo192.png`,
      "sameAs": [
        // Add known profiles or leave empty array
      ]
    },
    {
      "@context": "https://schema.org",
      "@type": "AboutPage",
      "name": "About – We Are Local",
      "url": canonical,
      "isPartOf": { "@type": "WebSite", "url": origin },
      "description":
        "A calm, ad-free local hub with neutral news, practical guides, and events—plus a personal site mode tuned to each reader.",
      "publisher": { "@type": "Organization", "name": "We Are Local", "url": origin }
    }
  ];

  return (
    <main className="about about-page">
      {/* SEO tags */}
      <SEO
        title={title}
        description={description}
        canonical={canonical}
        og={{
          type: "website",
          title,
          description,
          url: canonical,
          image: ogImage
        }}
        twitter={{
          card: "summary_large_image",
          title,
          description,
          image: ogImage
        }}
        jsonLd={jsonLd}
        siteName="We Are Local"
        robots="index,follow"
      />

      <div className="about-topbar" />

      {/* HERO */}
      <section className="about-hero container">
        <h1 className="about-title">About We Are Local</h1>
        <p className="about-subtitle">
            <p>
            We are an AI-powered news aggregator combined with real stories and insights from people. 
            </p><p>Our mission is simple: to deliver neutral news, practical guides, and useful event updates — written in a way that’s clear, positive, and easy to follow.
            </p><p>We’re a local community project designed for people who want to stay informed, understand what’s happening around them, and feel connected to their neighborhood — without getting dragged into bias or negativity.
            </p><p>
            🚫 No ads.</p><p>🚫 No pushing.</p><p>🚫 No propaganda.</p>
            Just the key facts, meaningful stories, and resources that matter most.
        </p>

        <div className="about-ctas">
          <a className="btn-pill btn-primary" href="/contact?type=co-author">
            Become a Co-Author
          </a>
          <a className="btn-pill btn-ghost" href="/contact?type=pitch">
            Pitch an Idea
          </a>
        </div>
      </section>

      <div className="container about-grid">
        {/* Mission */}
        <section className="card">
          <header className="card-head">
            <h2 className="card-title">Our Mission</h2>
          </header>
          <div className="card-body">
            <p className="lead">
              We connect our community by{" "}
              <strong>
                sharing trustworthy local news, practical guides, and thoughtful
                perspectives — without pressure, politics, or propaganda
              </strong>
              . We aim for calm, useful coverage that helps people think clearly,
              take part, and feel at home.
            </p>

            <ul className="checks">
              <li>Inform without inflaming; explain before you opine.</li>
              <li>Keep reporting neutral, sourced, and transparent.</li>
              <li>Cover events and everyday decisions that affect real lives.</li>
              <li>Make it easy for credible locals to contribute and co-author.</li>
              <li>Respect readers’ time: short, clear, practical.</li>
              <li>No ads, no push alerts, no dark patterns — ever.</li>
            </ul>
          </div>
        </section>

        {/* What we publish */}
        <section className="card">
          <header className="card-head">
            <h2 className="card-title">What We Publish (For Everyone)</h2>
          </header>
          <div className="card-body">
            <ul className="checks">
              <li>Local news briefs &amp; explainers (plain-language recaps)</li>
              <li>Service journalism: how-tos, guides, lists, resources</li>
              <li>Lifestyle, wellbeing, family &amp; kids content</li>
              <li>Business &amp; education spotlights with real-world context</li>
              <li>Events, parks &amp; recreation, arts, and things to do</li>
              <li>Photo essays, data visualizations, and practical maps</li>
              <li>Thoughtful essays &amp; first-person stories <em>(clearly labeled)</em></li>
              <li>Fun: puzzles, games, brain teasers, and community features</li>
            </ul>
          </div>
        </section>

        {/* Personal site mode */}
        <section className="card">
          <header className="card-head">
            <h2 className="card-title">Your Personal Site</h2>
          </header>
          <div className="card-body">
            <p className="lead">
              Every reader can set up a{" "}
              <strong>personal view of We Are Local</strong> — a calm homepage
              that shows only the topics, neighborhoods, and formats you want.
            </p>
            <ul className="bullets">
              <li>
                Choose your areas (city, county, neighborhood) and interests
                (schools, safety, transit, outdoors, kids, pets, finance, tech…).
              </li>
              <li>
                Pick formats you like: quick briefs, in-depth explainers, guides,
                events, puzzles — or all of the above.
              </li>
              <li>
                No algorithmic shoving: your settings decide what shows up — and
                you can change them anytime.
              </li>
              <li>
                <strong>No advertising, no pop-ups, no trackers, no pushing.</strong>
              </li>
            </ul>

            <div className="inline-ctas">
              <a className="btn-pill btn-primary" href="/contact?type=personalize">
                Set Up My Personal Site
              </a>
              <a className="btn-pill btn-ghost" href="/privacy">
                How We Handle Privacy
              </a>
            </div>
          </div>
        </section>

        {/* Editorial principles */}
        <section className="card">
          <header className="card-head">
            <h2 className="card-title">Editorial Principles</h2>
          </header>
          <div className="card-body two-col">
            <ul className="bullets">
              <li>
                <strong>Neutral by default:</strong> we separate reporting from
                commentary and label perspectives clearly.
              </li>
              <li>
                <strong>Source-first:</strong> cite the docs, link the data, name
                the officials.
              </li>
              <li>
                <strong>Corrections welcomed:</strong> when something’s off, we fix
                fast and note the change.
              </li>
            </ul>
            <ul className="bullets">
              <li>
                <strong>Community over clicks:</strong> usefulness beats outrage.
              </li>
              <li>
                <strong>Plain language:</strong> if grandma can’t follow it, we keep
                editing.
              </li>
              <li>
                <strong>Safety &amp; respect:</strong> moderated comments; no
                harassment, no spam, no disinfo.
              </li>
            </ul>
          </div>
        </section>

        {/* Co-author program */}
        <section className="card">
          <header className="card-head">
            <h2 className="card-title">Co-Author with Us</h2>
          </header>
          <div className="card-body">
            <p className="lead">
              Great local coverage comes from{" "}
              <strong>curious neighbors and careful editors</strong>. If you know a
              topic well — or just want to help — we’ll guide you from pitch to
              publish.
            </p>
            <ol className="steps">
              <li>
                <strong>Pitch</strong> a short idea (what’s new / useful / local).
              </li>
              <li>
                <strong>Draft</strong> with our outline template and style notes.
              </li>
              <li>
                <strong>Edit</strong> together for accuracy and clarity.
              </li>
              <li>
                <strong>Publish</strong> and share with your community.
              </li>
            </ol>

            <div className="inline-ctas">
              <a className="btn-pill btn-primary" href="/contact?type=co-author">
                Become a Co-Author
              </a>
              <a className="btn-pill btn-ghost" href="/contact?type=pitch">
                Pitch an Idea
              </a>
            </div>
          </div>
        </section>

        {/* Contact */}
        <section className="card">
          <header className="card-head">
            <h2 className="card-title">Say Hello</h2>
          </header>
          <div className="card-body">
            <p className="lead">
              Want to collaborate, share data, or suggest coverage that helps
              everyone? Let’s talk.
            </p>
            <div className="inline-ctas">
              <a className="btn-pill btn-primary" href="/contact">
                Contact Us
              </a>
              <a className="btn-pill btn-ghost" href="/privacy">
                Editorial &amp; Privacy Policy
              </a>
            </div>
          </div>
        </section>
      </div>
    </main>
  );
}